import React, { useState } from "react";
import { MemberRegistrationForm, MembershipType } from "../../types";
import { getMembershipPrice } from "../../utils/membership";

interface MembershipTypeStepProps {
  formData: MemberRegistrationForm;
  updateFormData: (updates: Partial<MemberRegistrationForm>) => void;
  onNext: () => void;
  onBack: () => void;
}

const MembershipTypeStep: React.FC<MembershipTypeStepProps> = ({
  formData,
  updateFormData,
  onNext,
  onBack,
}) => {
  const [sponsorTier, setSponsorTier] = useState<"GOLD" | "SILVER" | "BRONZE">(
    formData.sponsorTier || "GOLD",
  );

  const isSponsorType = (type: MembershipType) =>
    type === MembershipType.SPONSOR_GOLD ||
    type === MembershipType.SPONSOR_SILVER ||
    type === MembershipType.SPONSOR_BRONZE;

  const currentIsSponsor = isSponsorType(formData.membershipType);

  const handleSelect = (type: MembershipType) => {
    const updates: Partial<MemberRegistrationForm> = {
      membershipType: type,
      isSponsor: isSponsorType(type),
    };
    // Clear family members for non-family types
    if (type === MembershipType.ADULT || type === MembershipType.JUNIOR) {
      updates.familyMembers = [];
    }
    // Set sponsor tier
    if (isSponsorType(type)) {
      updates.sponsorTier =
        type === MembershipType.SPONSOR_GOLD
          ? "GOLD"
          : type === MembershipType.SPONSOR_SILVER
            ? "SILVER"
            : "BRONZE";
    } else {
      updates.sponsorTier = undefined;
    }
    updateFormData(updates);
  };

  const handleSponsorTierChange = (tier: "GOLD" | "SILVER" | "BRONZE") => {
    setSponsorTier(tier);
    const typeMap = {
      GOLD: MembershipType.SPONSOR_GOLD,
      SILVER: MembershipType.SPONSOR_SILVER,
      BRONZE: MembershipType.SPONSOR_BRONZE,
    };
    handleSelect(typeMap[tier]);
  };

  const isSelected = (type: MembershipType) => formData.membershipType === type;

  const cardClass = (selected: boolean) =>
    `border-2 rounded-xl p-5 cursor-pointer transition-all ${
      selected
        ? "border-fishing-blue bg-blue-50 shadow-md"
        : "border-gray-200 hover:border-fishing-blue hover:shadow-sm"
    }`;

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-800 mb-2">
        Choose Your Membership
      </h2>
      <p className="text-gray-600 mb-6">
        Select the membership type that suits you best
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Family */}
        <div
          onClick={() => handleSelect(MembershipType.FAMILY)}
          className={cardClass(isSelected(MembershipType.FAMILY))}
        >
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-lg font-semibold text-gray-800">
              👨‍👩‍👧‍👦 Family Membership
            </h3>
            <span className="text-fishing-green font-bold text-lg">
              from $70
            </span>
          </div>
          <p className="text-sm text-gray-600 mb-2">
            Family: from $70, up to $120 for 2 adults and 3 juniors, $25 per extra junior
          </p>
          <ul className="text-xs text-gray-500 space-y-1">
            <li>✓ Access to all club events & competitions</li>
            <li>✓ Club merchandise available from time to time</li>
          </ul>
        </div>

        {/* Adult */}
        <div
          onClick={() => handleSelect(MembershipType.ADULT)}
          className={cardClass(isSelected(MembershipType.ADULT))}
        >
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-lg font-semibold text-gray-800">
              🎣 Adult Individual
            </h3>
            <span className="text-fishing-green font-bold text-lg">
              ${getMembershipPrice(MembershipType.ADULT)}
            </span>
          </div>
          <p className="text-sm text-gray-600 mb-2">
            Single adult membership (17 or over on 1 January of the membership year)
          </p>
          <ul className="text-xs text-gray-500 space-y-1">
            <li>✓ Access to all club events & competitions</li>
            <li>✓ Club merchandise available from time to time</li>
          </ul>
        </div>

        {/* Junior */}
        <div
          onClick={() => handleSelect(MembershipType.JUNIOR)}
          className={cardClass(isSelected(MembershipType.JUNIOR))}
        >
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-lg font-semibold text-gray-800">
              🐟 Junior Individual
            </h3>
            <span className="text-fishing-green font-bold text-lg">
              ${getMembershipPrice(MembershipType.JUNIOR)}
            </span>
          </div>
          <p className="text-sm text-gray-600 mb-2">
            16 or under on 1 January of the membership year — adult guardian required
          </p>
          <ul className="text-xs text-gray-500 space-y-1">
            <li>✓ Access to junior events & competitions</li>
            <li>✓ Guardian must confirm waiver</li>
            <li>✓ Club merchandise available from time to time</li>
          </ul>
        </div>

        {/* Sponsor */}
        <div
          onClick={() => {
            if (!currentIsSponsor) handleSponsorTierChange(sponsorTier);
          }}
          className={`${cardClass(currentIsSponsor)} md:col-span-2`}
        >
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-lg font-semibold text-gray-800">
              ⭐ Sponsor Membership
            </h3>
          </div>
          <p className="text-sm text-gray-600 mb-4">
            Support the club and gain local exposure — all tiers include free
            family membership (2 adults + 3 juniors)
          </p>

          {!currentIsSponsor && (
            <div className="text-xs text-fishing-blue font-medium">
              Tap to choose a sponsor tier (Gold / Silver / Bronze) →
            </div>
          )}

          {currentIsSponsor && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {/* Gold */}
            <label
              className={`relative border-2 rounded-xl p-4 cursor-pointer transition-all ${
                isSelected(MembershipType.SPONSOR_GOLD)
                  ? "border-yellow-500 bg-yellow-50 shadow-md"
                  : "border-gray-200 hover:border-yellow-400"
              }`}
              onClick={(e) => e.stopPropagation()}
            >
              <input
                type="radio"
                name="sponsorTier"
                checked={isSelected(MembershipType.SPONSOR_GOLD)}
                onChange={() => handleSponsorTierChange("GOLD")}
                className="sr-only"
              />
              <div className="flex items-center justify-between mb-2">
                <span className="font-bold text-yellow-600">🥇 Gold</span>
                <span className="font-bold text-fishing-green">
                  $
                  {getMembershipPrice(
                    MembershipType.SPONSOR_GOLD,
                  ).toLocaleString()}
                  /yr
                </span>
              </div>
              <p className="text-xs text-gray-500 mb-2">
                Highly visible — directly associated with the club
              </p>
              <ul className="text-xs text-gray-500 space-y-1">
                <li>✓ Banner at all monthly competitions</li>
                <li>✓ Dedicated social media post</li>
                <li>✓ Priority placement on signage & digital</li>
                <li>✓ Mention in all newsletters</li>
                <li>✓ Verbal acknowledgement at events</li>
                <li>✓ Business merch at one comp day</li>
                <li>✓ Logo on club shirt & signage</li>
                <li>✓ Sponsor Appreciation Night invite</li>
                <li>✓ Promo material at one event</li>
                <li>✓ Certificate of appreciation</li>
                <li>✓ Free family membership</li>
              </ul>
            </label>

            {/* Silver */}
            <label
              className={`relative border-2 rounded-xl p-4 cursor-pointer transition-all ${
                isSelected(MembershipType.SPONSOR_SILVER)
                  ? "border-gray-400 bg-gray-50 shadow-md"
                  : "border-gray-200 hover:border-gray-400"
              }`}
              onClick={(e) => e.stopPropagation()}
            >
              <input
                type="radio"
                name="sponsorTier"
                checked={isSelected(MembershipType.SPONSOR_SILVER)}
                onChange={() => handleSponsorTierChange("SILVER")}
                className="sr-only"
              />
              <div className="flex items-center justify-between mb-2">
                <span className="font-bold text-gray-500">🥈 Silver</span>
                <span className="font-bold text-fishing-green">
                  $
                  {getMembershipPrice(
                    MembershipType.SPONSOR_SILVER,
                  ).toLocaleString()}
                  /yr
                </span>
              </div>
              <p className="text-xs text-gray-500 mb-2">
                Stronger local presence through club activities
              </p>
              <ul className="text-xs text-gray-500 space-y-1">
                <li>✓ Mention in all newsletters</li>
                <li>✓ Verbal acknowledgement at events</li>
                <li>✓ Business merch at one comp day</li>
                <li>✓ Logo on club shirt & signage</li>
                <li>✓ Sponsor Appreciation Night invite</li>
                <li>✓ Promo material at one event</li>
                <li>✓ Opportunity to sponsor a comp</li>
                <li>✓ Certificate of appreciation</li>
                <li>✓ Free family membership</li>
              </ul>
            </label>

            {/* Bronze */}
            <label
              className={`relative border-2 rounded-xl p-4 cursor-pointer transition-all ${
                isSelected(MembershipType.SPONSOR_BRONZE)
                  ? "border-amber-600 bg-amber-50 shadow-md"
                  : "border-gray-200 hover:border-amber-400"
              }`}
              onClick={(e) => e.stopPropagation()}
            >
              <input
                type="radio"
                name="sponsorTier"
                checked={isSelected(MembershipType.SPONSOR_BRONZE)}
                onChange={() => handleSponsorTierChange("BRONZE")}
                className="sr-only"
              />
              <div className="flex items-center justify-between mb-2">
                <span className="font-bold text-amber-700">🥉 Bronze</span>
                <span className="font-bold text-fishing-green">
                  $
                  {getMembershipPrice(
                    MembershipType.SPONSOR_BRONZE,
                  ).toLocaleString()}
                  /yr
                </span>
              </div>
              <p className="text-xs text-gray-500 mb-2">
                Support the community and gain local exposure
              </p>
              <ul className="text-xs text-gray-500 space-y-1">
                <li>✓ Name on Facebook & newsletter</li>
                <li>✓ Logo on club shirt & signage</li>
                <li>✓ Sponsor Appreciation Night invite</li>
                <li>✓ Promo material at one event</li>
                <li>✓ Opportunity to sponsor a comp</li>
                <li>✓ Certificate of appreciation</li>
                <li>✓ Free family membership</li>
              </ul>
            </label>
          </div>
          )}
        </div>
      </div>

      {/* Info notices */}
      {isSelected(MembershipType.JUNIOR) && (
        <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800">
          A parent or guardian will need to provide their details and confirm
          the waiver.
        </div>
      )}
      {currentIsSponsor && (
        <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
          Sponsor membership includes full family membership benefits (2 adults
          + up to 3 juniors, 16 or under on 1 January of the membership year).
        </div>
      )}

      <div className="flex justify-between mt-8">
        <button
          onClick={onBack}
          className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
        >
          Back
        </button>
        <button
          onClick={onNext}
          className="px-6 py-2 bg-fishing-blue hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default MembershipTypeStep;
